import { useMemo, useState } from "react";
import { addMonths, eachDayOfInterval, endOfMonth, endOfWeek, format, isSameMonth, startOfMonth, startOfWeek, subMonths } from "date-fns";
import { ptBR } from "date-fns/locale";
import { ChevronLeft, ChevronRight, Plus, CheckCircle2, Circle, Trash2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { PageHeader } from "../components/common";
import { FormDialog } from "../components/FormDialog";
import { useResource } from "../hooks/useResource";
import { CHANNELS, channelLabel } from "../lib/api";

const FIELDS = [
  { name: "title", label: "Título da publicação", required: true },
  { name: "date", label: "Data", type: "date", required: true, half: true },
  { name: "time", label: "Horário", type: "time", half: true },
  { name: "channel", label: "Canal", type: "select", options: CHANNELS.map((c) => ({ value: c.id, label: c.label })), default: "instagram" },
  { name: "notes", label: "Notas", type: "textarea", rows: 2 },
];
const ymd = (d) => format(d, "yyyy-MM-dd");
const color = (ch) => CHANNELS.find((c) => c.id === ch)?.color || "#D4AF37";

export default function CalendarPage() {
  const r = useResource("events");
  const [month, setMonth] = useState(new Date());
  const [sel, setSel] = useState(ymd(new Date()));
  const [dlg, setDlg] = useState(false);
  const days = useMemo(() => eachDayOfInterval({ start: startOfWeek(startOfMonth(month)), end: endOfWeek(endOfMonth(month)) }), [month]);
  const byDay = useMemo(() => r.items.reduce((m, e) => ({ ...m, [e.date]: [...(m[e.date] || []), e] }), {}), [r.items]);
  const dayEvents = (byDay[sel] || []).sort((a, b) => (a.time || "").localeCompare(b.time || ""));
  const toggle = (e) => r.save({ ...e, status: e.status === "publicado" ? "planejado" : "publicado" }, e.id);

  return (
    <div>
      <PageHeader eyebrow="Planejamento" title="Calendário editorial" desc="Planeje suas publicações e marque quando forem publicadas."
        action={<Button className="btn-gold rounded-full h-11 px-5" onClick={() => setDlg(true)} data-testid="event-add-button"><Plus className="h-4 w-4 mr-1" />Agendar</Button>} />
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="card-lux p-4 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <button className="p-2 rounded-lg hover:bg-zinc-800" onClick={() => setMonth(subMonths(month, 1))} data-testid="calendar-prev"><ChevronLeft className="h-4 w-4" /></button>
            <div className="font-display text-lg font-bold capitalize" data-testid="calendar-month">{format(month, "MMMM yyyy", { locale: ptBR })}</div>
            <button className="p-2 rounded-lg hover:bg-zinc-800" onClick={() => setMonth(addMonths(month, 1))} data-testid="calendar-next"><ChevronRight className="h-4 w-4" /></button>
          </div>
          <div className="grid grid-cols-7 gap-1 text-center text-[10px] text-zinc-500 mb-1">{["D", "S", "T", "Q", "Q", "S", "S"].map((d, i) => <div key={i}>{d}</div>)}</div>
          <div className="grid grid-cols-7 gap-1">
            {days.map((d) => {
              const k = ymd(d), evs = byDay[k] || [];
              return (
                <button key={k} onClick={() => setSel(k)} data-testid={`calendar-day-${k}`}
                  className={`aspect-square sm:aspect-[4/3] rounded-xl p-1 sm:p-2 text-left border transition-colors ${sel === k ? "border-amber-500/60 bg-amber-500/10" : "border-zinc-800/60 hover:border-zinc-700"} ${isSameMonth(d, month) ? "" : "opacity-30"}`}>
                  <div className={`text-xs ${k === ymd(new Date()) ? "text-amber-400 font-bold" : "text-zinc-300"}`}>{format(d, "d")}</div>
                  <div className="flex flex-wrap gap-0.5 mt-1">{evs.slice(0, 4).map((e) => <span key={e.id} className="h-1.5 w-1.5 rounded-full" style={{ background: color(e.channel) }} />)}</div>
                </button>
              );
            })}
          </div>
        </div>
        <div className="card-lux p-4" data-testid="calendar-day-panel">
          <div className="eyebrow mb-3">{format(new Date(sel + "T12:00:00"), "EEEE, d 'de' MMMM", { locale: ptBR })}</div>
          {dayEvents.length === 0 ? <p className="text-sm text-zinc-500">Nenhuma publicação neste dia.</p> : dayEvents.map((e) => (
            <div key={e.id} className="flex items-center gap-2 py-2.5 border-b border-zinc-800/70 last:border-0" data-testid={`event-row-${e.id}`}>
              <button onClick={() => toggle(e)} data-testid={`event-toggle-${e.id}`}>{e.status === "publicado" ? <CheckCircle2 className="h-5 w-5 text-emerald-400" /> : <Circle className="h-5 w-5 text-zinc-600" />}</button>
              <div className="flex-1 min-w-0"><div className={`text-sm truncate ${e.status === "publicado" ? "line-through text-zinc-500" : ""}`}>{e.title}</div><div className="text-[11px]" style={{ color: color(e.channel) }}>{channelLabel(e.channel)} {e.time && `• ${e.time}`}</div></div>
              <button className="p-1 text-zinc-600 hover:text-rose-400" onClick={() => r.remove(e.id)} data-testid={`event-delete-${e.id}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      </div>
      <FormDialog open={dlg} onOpenChange={setDlg} title="Agendar publicação" fields={FIELDS} initial={{ date: sel, channel: "instagram" }} testid="event-form" onSubmit={(b) => r.save({ ...b, status: "planejado" })} />
    </div>
  );
}
